import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { copyObject, isAlive } from 'webapp/utils/common';

import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormSelectWithActionComponent from 'webapp/components/form/select-with-action';
import SourceConnectButtonComponent from 'webapp/components/source/connect/button';

import type { Metric } from 'webapp/models-custom';
import type SourceType from 'webapp/models-custom/source-type';

import type { AfterConnectFn } from 'webapp/services/connect';
import type { SourceListEntry } from 'webapp/services/model-manager/sources';

interface Args {
	sourceType: SourceType | null | undefined;
	selectedMetrics: Metric[];
	onSourceSelected: (sourceId: number) => void;
	onConnect: (sourceType: SourceType, afterConnect?: AfterConnectFn) => void;
	onOpenRequestModal: (sourceType: SourceType) => void;
	onTriggerNextStep: () => void;
}

export default class WizardConnectSourceComponent extends Component<Args> {
	@service('connect') declare connectService: Service.Connect;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('flow/tour/onboarding')
	declare flowTourOnboardingService: Service.Flow.Tour.Onboarding;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('wizard') declare wizardService: Service.Wizard;

	@tracked selectedSourceId: number | null = null;
	@tracked metrics: Metric[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const sourceType = this.args.sourceType;
		if (sourceType) {
			this.metrics = this.wizardService.getMetricsForSource(sourceType);
		}
	}

	get selectedSourceIdString() {
		return this.selectedSourceId ? String(this.selectedSourceId) : null;
	}

	get metricNames() {
		return this.args.selectedMetrics
			.map((metric) => {
				return metric.name;
			})
			.join(', ');
	}

	get selectedMetricsCountText() {
		const selectedCount = this.args.selectedMetrics.length;
		return `${selectedCount} metric` + (selectedCount > 1 ? 's' : '') + ' selected: ';
	}

	get hasAnyConnectedSource() {
		return !!this.alreadyConnectedSources.length;
	}

	get alreadyConnectedSources() {
		const sourceType = this.args.sourceType;

		if (!sourceType) {
			return [];
		}

		const typeSources = this.mmSourcesService.sourceListSpecific.filter(
			(item) => item.type === (sourceType?.id ?? undefined),
		);

		// make a copy
		const sources: (SourceListEntry & { disabled?: boolean })[] = copyObject(typeSources);

		sources.forEach((source) => {
			source.disabled = !!source.isDisabled; // the select only checks it for truthiness
			source.avatar = '';
			source.group = '';
		});

		return sources;
	}

	get sourceTypeName() {
		return this.args.sourceType?.name;
	}

	get sourceActions() {
		if (this.flowTourOnboardingService.isBannerShown) {
			return [];
		}

		const sourceTypeName = this.sourceTypeName;

		return [
			{
				id: null,
				text: 'Connect another ' + sourceTypeName,
				html: '<a class="source-add">Connect another ' + sourceTypeName + '</a>',
				isSpecial: true,
				position: 'bottom',
				onClick: () => {
					this.connectSource();
				},
			},
		];
	}

	@action
	connectSource() {
		const sourceType = this.args.sourceType;

		if (!sourceType) {
			return;
		}

		this.mmSourceTypesService.triggerNewConnectionInitiated(sourceType, 'wizard');
		this.args.onConnect(sourceType, (isSuccess, source) => {
			if (isSuccess && source && isAlive(this)) {
				this.selectSource(source.id);
			}
		});
	}

	@action
	selectSource(sourceId: string | number | null) {
		this.selectedSourceId = +sourceId!;
		this.args.onSourceSelected(this.selectedSourceId);
		this.args.onTriggerNextStep(); // Triggering after selecting already connected source or after connecting new source (action function connectSource above).
	}
	<template>
		<div class="main info">
			<AvatarOrInitialsComponent @name={{@sourceType.name}} @avatar={{@sourceType.avatar}} />
			<div class="info-wrapper">
				<div class="details">
					<p class="name">{{@sourceType.name}}</p>
					<p class="description">
						<strong>{{this.selectedMetricsCountText}}</strong>
						{{this.metricNames}}
					</p>
				</div>
				<div class="actions">
					{{#if this.hasAnyConnectedSource}}
						<FormSelectWithActionComponent
							name="source"
							@selected={{this.selectedSourceIdString}}
							@options={{this.alreadyConnectedSources}}
							@actionOptions={{this.sourceActions}}
							@onChange={{this.selectSource}}
							@placeholder="Select data source"
							@isNumeric={{false}}
							@isRequired={{true}}
							@showFloatlabel={{false}}
						/>
					{{else}}
						{{#if @sourceType.showConnectHelp}}
							<a
								role="button"
								class="connect-help button size-big theme-blue-outlined
									{{unless this.currentUserService.model.isVerified 'disabled'}}"
								{{on "click" (fn @onOpenRequestModal @sourceType)}}
							>
								<CommonUiIconComponent @name="Mail" @size="sm" />Ask teammate to
								connect
							</a>
						{{/if}}
						{{#if @sourceType}}
							<SourceConnectButtonComponent
								@sourceType={{@sourceType}}
								@onConnect={{this.connectSource}}
								@extraClass="next"
								@size="big"
								@noDelay={{true}}
								@theme="green"
							/>
						{{/if}}
					{{/if}}
				</div>
			</div>
		</div>
	</template>
}
